import { useState, useEffect } from 'react';
import { API_BASE_URL } from './config';
import { channelColors, channelBorderColors } from './RoomManagement/utils';
import { useResponsive } from './hooks/useResponsive';

interface Member {
  id: string;
  name: string;
  email: string;
  phone: string;
  room_count: number;
  booking_count: number;
  total_settlement: number;
  occupancy_rate: number | null;
  role: string;
  is_collaborator: boolean;
  created_at: string;
  last_login_at: string;
  primary_address?: string;
  primary_room_type?: string;
  room_types?: string[];
  room_addresses?: string[];
  operation_starts?: string[];
  earliest_booking?: string;
}

interface OnlineUser {
  ip: string;
  label: string;
  requests: number;
  last_seen: string;
  name: string;
}

interface BookingItem {
  room_name: string;
  check_in: string;
  check_out: string;
  channel: string;
  guest_name: string;
  days: number;
}

interface RoomItem {
  id: string;
  name: string;
  address: string;
  detail_address: string;
  building_name: string;
  room_type: string;
  status: string;
}

interface SettlementItem {
  room_name: string;
  channel: string;
  check_in: string;
  check_out: string;
  days: number;
  final_price: number;
  payment_status: string;
}

interface SettlementData {
  member: Member;
  items: SettlementItem[];
  total: number;
  settled: number;
  pending: number;
}

interface MemberListTabProps {
  token: string;
}

function parseRegion(address?: string): string {
  if (!address) return '-';
  const parts = address.split(' ').filter(Boolean);
  const result: string[] = [];
  for (const p of parts) {
    if (/시$|도$|군$|구$|동$|읍$|면$/.test(p)) {
      result.push(p);
      if (/동$|읍$|면$/.test(p)) break;
    }
  }
  return result.length ? result.join(' ') : (parts.slice(0, 2).join(' ') || '-');
}

function utcDate(iso: string): Date {
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  return new Date(s);
}

function maskName(name: string): string {
  if (!name || name.length <= 1) return name;
  if (name.length === 2) return name[0] + 'O';
  return name[0] + 'O'.repeat(name.length - 2) + name[name.length - 1];
}

function maskPhone(phone: string): string {
  if (!phone || phone === '-') return phone;
  const parts = phone.split('-');
  if (parts.length === 3) return `${parts[0]}-XXXX-${parts[2]}`;
  return phone.replace(/(\d{3,4})(?=\d{4}$)/, 'XXXX');
}

function formatPhone(phone: string): string {
  if (!phone) return '-';
  if (phone.includes('-')) return phone;
  const d = phone.replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10 && d.startsWith('02')) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return phone;
}

export default function MemberListTab({ token }: MemberListTabProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlineCount, setOnlineCount] = useState<number | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [showOnlinePopup, setShowOnlinePopup] = useState(false);

  const [bookingPopup, setBookingPopup] = useState<{ member: Member; bookings: BookingItem[] } | null>(null);
  const [roomPopup, setRoomPopup] = useState<{ member: Member; rooms: RoomItem[] } | null>(null);
  const [roomLoading, setRoomLoading] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ address: string; detail_address: string; room_type: string }>({ address: '', detail_address: '', room_type: '' });
  const { isMobile } = useResponsive();
  const [settlementPopup, setSettlementPopup] = useState<SettlementData | null>(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [isUnmasked, setIsUnmasked] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [superPwInput, setSuperPwInput] = useState('');
  const [superPwError, setSuperPwError] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Member | null>(null);
  const [deletePwInput, setDeletePwInput] = useState('');
  const [deletePwError, setDeletePwError] = useState(false);

  const displayName = (name: string) => isUnmasked ? name : maskName(name);
  const displayPhone = (phone: string) => isUnmasked ? formatPhone(phone) : maskPhone(formatPhone(phone));

  const handleSuperPw = () => {
    if (superPwInput === 'agbnmr2@') {
      setIsUnmasked(true); setShowPwModal(false); setSuperPwInput(''); setSuperPwError(false);
    } else {
      setSuperPwError(true);
    }
  };

  useEffect(() => {
    const headers = { 'Authorization': `Bearer ${token}` };
    Promise.all([
      fetch(`${API_BASE_URL}/api/str-users/list`, { headers }).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/admin/online-count`, { headers }).then(r => r.json()),
    ]).then(([userData, onlineData]) => {
      setMembers(userData.users || []);
      setOnlineCount(onlineData.count ?? 0);
      setOnlineUsers(onlineData.users || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  const openBookingPopup = (m: Member) => {
    if (m.booking_count === 0) return;
    setBookingLoading(true);
    setBookingPopup({ member: m, bookings: [] });
    fetch(`${API_BASE_URL}/api/admin/users/${m.id}/bookings`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setBookingPopup({ member: m, bookings: data.bookings || [] });
        setBookingLoading(false);
      })
      .catch(() => setBookingLoading(false));
  };

  const openSettlementPopup = (m: Member) => {
    if (m.total_settlement <= 0) return;
    setSettlementLoading(true);
    setSettlementPopup({ member: m, items: [], total: 0, settled: 0, pending: 0 });
    fetch(`${API_BASE_URL}/api/admin/users/${m.id}/settlements`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setSettlementPopup({ member: m, items: data.items || [], total: data.total, settled: data.settled, pending: data.pending });
        setSettlementLoading(false);
      })
      .catch(() => setSettlementLoading(false));
  };

  const startEditRoom = (r: RoomItem) => {
    setEditingRoomId(r.id);
    setEditForm({ address: r.address, detail_address: r.detail_address, room_type: r.room_type });
  };

  const saveRoomEdit = async (roomId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/rooms/${roomId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        setRoomPopup(prev => prev ? {
          ...prev,
          rooms: prev.rooms.map(r => r.id === roomId
            ? { ...r, address: editForm.address, detail_address: editForm.detail_address, room_type: editForm.room_type }
            : r
          ),
        } : null);
        setEditingRoomId(null);
      }
    } catch (e) {}
  };

  const openRoomPopup = (m: Member) => {
    if (m.room_count === 0) return;
    setRoomLoading(true);
    setRoomPopup({ member: m, rooms: [] });
    fetch(`${API_BASE_URL}/api/admin/users/${m.id}/rooms`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setRoomPopup({ member: m, rooms: data.rooms || [] });
        setRoomLoading(false);
      })
      .catch(() => setRoomLoading(false));
  };

  const handleDelete = (m: Member) => {
    if (m.role === 'admin') return;
    setPendingDelete(m);
    setDeletePwInput('');
    setDeletePwError(false);
  };

  const confirmDelete = async () => {
    if (deletePwInput !== 'agbnmr2@') { setDeletePwError(true); return; }
    if (!pendingDelete) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-users/${pendingDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setMembers(prev => prev.filter(x => x.id !== pendingDelete.id));
        setPendingDelete(null);
      } else {
        const data = await res.json();
        alert(data.detail || '삭제에 실패했습니다.');
      }
    } catch {
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>로딩 중...</div>;

  return (
    <div style={{ padding: '20px 0' }}>
      {/* 접속자 팝업 */}
      {showOnlinePopup && (
        <div
          onClick={() => setShowOnlinePopup(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,.15)', padding: '20px 24px', width: 'min(92vw, 360px)', fontSize: 13 }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>최근 15분 접속</div>
            {onlineUsers.length === 0 ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '12px 0' }}>15분 내 접속 없음</div>
            ) : (
              onlineUsers.map((u, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      fontSize: 10, padding: '1px 5px', borderRadius: 3,
                      background: u.label === '내부망' ? '#e8f5e9' : '#fff3e0',
                      color: u.label === '내부망' ? '#2e7d32' : '#e65100',
                    }}>{u.label}</span>
                    <span style={{ color: '#334155' }}>
                      {u.name || u.ip}
                      {u.name && <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 4 }}>{u.ip}</span>}
                    </span>
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.requests}회 · {u.last_seen}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 계약 내역 팝업 */}
      {bookingPopup && (
        <div
          onClick={() => setBookingPopup(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: '22px 26px', width: 'min(92vw, 440px)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{bookingPopup.member.name}</span>
                <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>계약 {bookingPopup.member.booking_count}건</span>
              </div>
              <button
                onClick={() => setBookingPopup(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
              >×</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {bookingLoading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>로딩 중...</div>
              ) : bookingPopup.bookings.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>계약 내역이 없습니다.</div>
              ) : (
                bookingPopup.bookings.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: 11, color: '#cbd5e1', minWidth: 20, textAlign: 'right', flexShrink: 0 }}>{i + 1}.</span>
                    {b.channel && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                        background: channelColors[b.channel] || '#f1f5f9',
                        color: channelBorderColors[b.channel] || '#64748b',
                        border: `1px solid ${channelBorderColors[b.channel] || '#e2e8f0'}`,
                      }}>{b.channel}</span>
                    )}
                    <span style={{ fontSize: 13, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                      {b.check_in} ~ {b.check_out}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{b.days}일</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* 방 목록 팝업 */}
      {roomPopup && (
        <div
          onClick={() => setRoomPopup(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: '22px 26px', width: 'min(92vw, 480px)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{roomPopup.member.name}</span>
                <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>등록 방 {roomPopup.member.room_count}개</span>
              </div>
              <button
                onClick={() => setRoomPopup(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
              >×</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {roomLoading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>로딩 중...</div>
              ) : roomPopup.rooms.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>등록된 방이 없습니다.</div>
              ) : (
                roomPopup.rooms.map((r, i) => (
                  <div key={r.id} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                    {/* 방 헤더 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: '#cbd5e1', minWidth: 20, textAlign: 'right' }}>{i + 1}.</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{r.name}</span>
                      {editingRoomId !== r.id && (
                        <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#f1f5f9', color: '#64748b' }}>{r.room_type}</span>
                      )}
                      <button
                        onClick={() => editingRoomId === r.id ? setEditingRoomId(null) : startEditRoom(r)}
                        style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #e2e8f0', background: editingRoomId === r.id ? '#f1f5f9' : '#fff', color: '#64748b', cursor: 'pointer' }}
                      >{editingRoomId === r.id ? '취소' : '수정'}</button>
                    </div>

                    {editingRoomId === r.id ? (
                      /* 수정 폼 */
                      <div style={{ marginLeft: 28, display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 2 }}>건물 타입</label>
                          <select
                            value={editForm.room_type}
                            onChange={e => setEditForm(f => ({ ...f, room_type: e.target.value }))}
                            style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 4, outline: 'none' }}
                          >
                            {['원룸', '아파트', '오피스텔', '빌라', '단독주택', '고시원'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 2 }}>주소</label>
                          <input
                            value={editForm.address}
                            onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                            style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 4, outline: 'none', boxSizing: 'border-box' }}
                            placeholder="도로명 주소"
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 2 }}>상세주소</label>
                          <input
                            value={editForm.detail_address}
                            onChange={e => setEditForm(f => ({ ...f, detail_address: e.target.value }))}
                            style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 4, outline: 'none', boxSizing: 'border-box' }}
                            placeholder="동/호수"
                          />
                        </div>
                        <button
                          onClick={() => saveRoomEdit(r.id)}
                          style={{ alignSelf: 'flex-end', fontSize: 12, padding: '4px 14px', borderRadius: 4, border: 'none', background: '#6B4EFF', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                        >저장</button>
                      </div>
                    ) : (
                      /* 보기 모드 */
                      <div style={{ marginLeft: 28, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                        <div>{r.address}</div>
                        {r.detail_address && <div style={{ color: '#334155', fontWeight: 500 }}>{r.detail_address}</div>}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* 정산 세부내역 팝업 */}
      {settlementPopup && (
        <div
          onClick={() => setSettlementPopup(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: '22px 26px', width: 'min(92vw, 520px)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{settlementPopup.member.name}</span>
                <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>정산 내역</span>
              </div>
              <button
                onClick={() => setSettlementPopup(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
              >×</button>
            </div>
            {/* 요약 */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 14, fontSize: 12 }}>
              <div style={{ flex: 1, padding: '8px 10px', background: '#f8fafc', borderRadius: 6, textAlign: 'center' }}>
                <div style={{ color: '#94a3b8' }}>합계</div>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>{settlementPopup.total.toLocaleString('ko-KR')}원</div>
              </div>
              <div style={{ flex: 1, padding: '8px 10px', background: '#f0fdf4', borderRadius: 6, textAlign: 'center' }}>
                <div style={{ color: '#16a34a' }}>정산 완료</div>
                <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 13 }}>{settlementPopup.settled.toLocaleString('ko-KR')}원</div>
              </div>
              <div style={{ flex: 1, padding: '8px 10px', background: '#fff7ed', borderRadius: 6, textAlign: 'center' }}>
                <div style={{ color: '#ea580c' }}>정산 예정</div>
                <div style={{ fontWeight: 700, color: '#ea580c', fontSize: 13 }}>{settlementPopup.pending.toLocaleString('ko-KR')}원</div>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {settlementLoading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>로딩 중...</div>
              ) : settlementPopup.items.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>정산 내역이 없습니다.</div>
              ) : (
                settlementPopup.items.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                    <span style={{ color: '#cbd5e1', minWidth: 20, textAlign: 'right', flexShrink: 0 }}>{i + 1}.</span>
                    {s.channel && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                        background: channelColors[s.channel] || '#f1f5f9',
                        color: channelBorderColors[s.channel] || '#64748b',
                        border: `1px solid ${channelBorderColors[s.channel] || '#e2e8f0'}`,
                      }}>{s.channel}</span>
                    )}
                    <span style={{ color: '#334155', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                      {s.check_in} ~ {s.check_out}
                    </span>
                    <span style={{ color: '#94a3b8', flexShrink: 0 }}>{s.days}일</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                      {s.final_price.toLocaleString('ko-KR')}원
                    </span>
                    <span style={{
                      fontSize: 10, padding: '1px 5px', borderRadius: 3, flexShrink: 0,
                      background: s.payment_status === 'completed' ? '#dcfce7' : '#fff7ed',
                      color: s.payment_status === 'completed' ? '#16a34a' : '#ea580c',
                    }}>{s.payment_status === 'completed' ? '완료' : '예정'}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* 헤더 */}
      {(() => {
        const allAddresses = members.flatMap(m => m.room_addresses || []);
        const regionCount = (keyword: string) => allAddresses.filter(a => a.includes(keyword)).length;
        const majorRegions = ['서울', '경기', '인천', '부산', '대전', '대구', '광주', '울산'];
        const etcCount = allAddresses.filter(a => a && !majorRegions.some(r => a.includes(r))).length;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 16px', flexWrap: 'wrap', gap: 8 }}>
            {/* 좌측: 제목 + 인원 + 건물타입 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>회원 목록</h2>
              <div style={{ display: 'flex', gap: 8, fontSize: 13, color: '#64748b', alignItems: 'center', flexWrap: 'wrap' }}>
                <span>인원 <b style={{ color: '#0f172a' }}>{members.length}</b>명</span>
                <span onClick={() => onlineCount !== null && setShowOnlinePopup(true)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  접속중 <b style={{ color: onlineCount !== null && onlineCount > 0 ? '#e53935' : '#94a3b8' }}>{onlineCount !== null ? onlineCount : '-'}</b>명
                </span>
                <span>방 <b style={{ color: '#2563eb' }}>{members.reduce((sum, m) => sum + m.room_count, 0)}</b>개</span>
                {(['원룸', '오피스텔', '아파트', '투룸', '생숙'] as const).map(type => {
                  const cnt = members.reduce((sum, m) => sum + (m.room_types || []).filter(t => t === type).length, 0);
                  return cnt > 0 ? <span key={type}>{type} <b style={{ color: '#0f172a' }}>{cnt}</b></span> : null;
                })}
              </div>
            </div>
            {/* 우측: 지역별 + 계약/정산 + 마스킹 */}
            <div style={{ display: 'flex', gap: 8, fontSize: 13, color: '#64748b', flexWrap: 'wrap', alignItems: 'center' }}>
              {majorRegions.map(r => {
                const cnt = regionCount(r);
                return cnt > 0 ? <span key={r}>{r} <b style={{ color: '#0f172a' }}>{cnt}</b></span> : null;
              })}
              {etcCount > 0 && <span>그외 <b style={{ color: '#0f172a' }}>{etcCount}</b></span>}
              <span>전체 계약수 <b style={{ color: '#0f172a' }}>{members.reduce((sum, m) => sum + m.booking_count, 0)}</b>건</span>
              <span>전체 정산금 <b style={{ color: '#16a34a' }}>{members.reduce((sum, m) => sum + m.total_settlement, 0).toLocaleString()}</b>원</span>
              <button
                onClick={() => isUnmasked ? setIsUnmasked(false) : setShowPwModal(true)}
                style={{ padding: '3px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0', background: isUnmasked ? '#fef2f2' : '#f8fafc', color: isUnmasked ? '#dc2626' : '#64748b', cursor: 'pointer' }}
              >{isUnmasked ? '🔓 마스킹 복원' : '🔒 마스킹 해제'}</button>
            </div>
          </div>
        );
      })()}

      {/* 슈퍼 비밀번호 모달 */}
      {showPwModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowPwModal(false); setSuperPwInput(''); setSuperPwError(false); } }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 300, boxSizing: 'border-box' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700 }}>🔐 마스킹 해제</h3>
            <input
              type="password"
              placeholder="슈퍼 비밀번호 입력"
              value={superPwInput}
              onChange={(e) => { setSuperPwInput(e.target.value); setSuperPwError(false); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSuperPw()}
              autoFocus
              style={{ width: '100%', padding: '9px 12px', border: `1px solid ${superPwError ? '#ef4444' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 6 }}
            />
            {superPwError && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>비밀번호가 틀렸습니다</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => { setShowPwModal(false); setSuperPwInput(''); setSuperPwError(false); }} style={{ padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>취소</button>
              <button onClick={handleSuperPw} style={{ padding: '7px 14px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {pendingDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setPendingDelete(null); setDeletePwInput(''); setDeletePwError(false); } }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 320, boxSizing: 'border-box' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#dc2626' }}>⚠️ 회원 삭제</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#475569' }}>
              <b>{pendingDelete.name}</b> 회원과 모든 방·예약 데이터가 삭제됩니다.<br />슈퍼 비밀번호를 입력하세요.
            </p>
            <input
              type="password"
              placeholder="슈퍼 비밀번호 입력"
              value={deletePwInput}
              onChange={(e) => { setDeletePwInput(e.target.value); setDeletePwError(false); }}
              onKeyDown={(e) => e.key === 'Enter' && confirmDelete()}
              autoFocus
              style={{ width: '100%', padding: '9px 12px', border: `1px solid ${deletePwError ? '#ef4444' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 6 }}
            />
            {deletePwError && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>비밀번호가 틀렸습니다</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => { setPendingDelete(null); setDeletePwInput(''); setDeletePwError(false); }} style={{ padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f5f5f5', fontSize: 13, cursor: 'pointer' }}>취소</button>
              <button onClick={confirmDelete} style={{ padding: '7px 14px', border: 'none', borderRadius: 8, background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>삭제</button>
            </div>
          </div>
        </div>
      )}

      {isMobile ? (
        /* ── 모바일: 카드 레이아웃 ── */
        <div>
          {members.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>가입된 회원이 없습니다.</div>
          ) : members.map((m, i) => (
            <div key={m.id} style={{ background: '#fff', border: '1px solid #f1f5f9', borderRadius: 10, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
              {/* 이름 + 역할 + 삭제 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: '#cbd5e1', minWidth: 18 }}>{i + 1}.</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{displayName(m.name)}</span>
                {m.role === 'admin' ? (
                  <span style={{ fontSize: 10, backgroundColor: '#6B4EFF', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>관리자</span>
                ) : m.is_collaborator ? (
                  <span style={{ fontSize: 10, backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>공동관리</span>
                ) : (
                  <span style={{ fontSize: 10, backgroundColor: '#f1f5f9', color: '#64748b', padding: '2px 6px', borderRadius: 4 }}>호스트</span>
                )}
                {m.role !== 'admin' && (
                  <button
                    onClick={() => handleDelete(m)}
                    style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', cursor: 'pointer' }}
                  >삭제</button>
                )}
              </div>
              {/* 전화 · 이메일 */}
              <div style={{ fontSize: 12, color: '#475569', paddingLeft: 22, marginBottom: 7, lineHeight: 1.6 }}>
                <div>{displayPhone(m.phone)}</div>
                {m.email && <div style={{ color: '#94a3b8' }}>{m.email}</div>}
              </div>
              {/* 통계 */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingLeft: 22, marginBottom: 6, alignItems: 'center' }}>
                <span
                  onClick={() => openRoomPopup(m)}
                  style={{ fontSize: 12, color: m.room_count > 0 ? '#2563eb' : '#94a3b8', cursor: m.room_count > 0 ? 'pointer' : 'default', textDecoration: m.room_count > 0 ? 'underline' : 'none', fontWeight: 600 }}
                >방 {m.room_count}개</span>
                {m.primary_room_type && (
                  <span style={{ fontSize: 11, color: '#475569', background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{m.primary_room_type}</span>
                )}
                {parseRegion(m.primary_address) !== '-' && (
                  <span style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', padding: '1px 5px', borderRadius: 4 }}>{parseRegion(m.primary_address)}</span>
                )}
                {m.occupancy_rate !== null && m.occupancy_rate !== undefined && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                    background: m.occupancy_rate >= 70 ? '#dcfce7' : m.occupancy_rate >= 40 ? '#fef9c3' : '#fee2e2',
                    color: m.occupancy_rate >= 70 ? '#16a34a' : m.occupancy_rate >= 40 ? '#a16207' : '#dc2626',
                  }}>{m.occupancy_rate}%</span>
                )}
                <span
                  onClick={() => openBookingPopup(m)}
                  style={{ fontSize: 12, color: m.booking_count > 0 ? '#2563eb' : '#94a3b8', cursor: m.booking_count > 0 ? 'pointer' : 'default', textDecoration: m.booking_count > 0 ? 'underline' : 'none' }}
                >계약 {m.booking_count}건</span>
                {m.total_settlement > 0 && (
                  <span
                    onClick={() => openSettlementPopup(m)}
                    style={{ fontSize: 12, color: '#334155', cursor: 'pointer', textDecoration: 'underline' }}
                  >{m.total_settlement.toLocaleString('ko-KR')}원</span>
                )}
              </div>
              {/* 날짜 */}
              <div style={{ fontSize: 11, color: '#94a3b8', paddingLeft: 22, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {m.created_at && <span>가입 {utcDate(m.created_at).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' })}</span>}
                {m.last_login_at && <span>마지막 로그인 {utcDate(m.last_login_at).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' })} {utcDate(m.last_login_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── 데스크탑: 테이블 레이아웃 ── */
        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
          <colgroup>
            <col style={{ width: 36 }} />{/* No */}
            <col style={{ width: 80 }} />{/* 성명 */}
            <col style={{ width: 120 }} />{/* 전화번호 */}
            <col />{/* 이메일 - 나머지 공간 */}
            <col style={{ width: 36 }} />{/* 방 */}
            <col style={{ width: 68 }} />{/* 운영시작 */}
            <col style={{ width: 62 }} />{/* 건물타입 */}
            <col style={{ width: 62 }} />{/* 지역 */}
            <col style={{ width: 58 }} />{/* 가동률 */}
            <col style={{ width: 40 }} />{/* 계약 */}
            <col style={{ width: 118 }} />{/* 누적 정산금 */}
            <col style={{ width: 58 }} />{/* 역할 */}
            <col style={{ width: 72 }} />{/* 가입일 */}
            <col style={{ width: 98 }} />{/* 마지막 로그인 */}
            <col style={{ width: 44 }} />{/* 삭제 */}
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#64748b' }}>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'left' }}>No</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>성명</th>
              <th style={{ padding: '10px 8px 10px 0', fontWeight: 600, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>전화번호</th>
              <th style={{ padding: '10px 8px 10px 0', fontWeight: 600, textAlign: 'left' }}>이메일</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center' }}>방</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>운영시작</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>건물타입</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>지역</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center' }}>가동률</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center' }}>계약</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'right' }}>누적 정산금</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center' }}>역할</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center' }}>가입일</th>
              <th style={{ padding: '10px 8px', fontWeight: 600, textAlign: 'center' }}>마지막 로그인</th>
              <th style={{ padding: '10px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan={16} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>가입된 회원이 없습니다.</td></tr>
            ) : members.map((m, i) => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <td style={{ padding: '12px 8px', color: '#94a3b8' }}>{i + 1}</td>
                <td style={{ padding: '12px 8px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName(m.name)}</td>
                <td style={{ padding: '12px 8px 12px 0', color: '#475569', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayPhone(m.phone)}</td>
                <td style={{ padding: '12px 8px 12px 0', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>{m.email}</td>
                <td
                  onClick={() => openRoomPopup(m)}
                  style={{
                    padding: '12px 8px', textAlign: 'center', fontWeight: 600,
                    color: m.room_count > 0 ? '#2563eb' : '#94a3b8',
                    cursor: m.room_count > 0 ? 'pointer' : 'default',
                    textDecoration: m.room_count > 0 ? 'underline' : 'none',
                  }}
                >{m.room_count}</td>
                <td style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(() => {
                    const opStart = (m.operation_starts || []).find(s => s) || m.earliest_booking || '';
                    if (!opStart) return <span style={{ color: '#cbd5e1' }}>-</span>;
                    return opStart.slice(2).replace(/-/g, '.');
                  })()}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: 11, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.primary_room_type ? (
                    <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{m.primary_room_type}</span>
                  ) : <span style={{ color: '#cbd5e1' }}>-</span>}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: 11, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{parseRegion(m.primary_address)}</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  {m.occupancy_rate !== null && m.occupancy_rate !== undefined ? (
                    <span style={{
                      display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 5px', borderRadius: 4,
                      background: m.occupancy_rate >= 70 ? '#dcfce7' : m.occupancy_rate >= 40 ? '#fef9c3' : '#fee2e2',
                      color: m.occupancy_rate >= 70 ? '#16a34a' : m.occupancy_rate >= 40 ? '#a16207' : '#dc2626',
                    }}>{m.occupancy_rate}%</span>
                  ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>-</span>}
                </td>
                <td
                  onClick={() => openBookingPopup(m)}
                  style={{
                    padding: '12px 8px', textAlign: 'center', fontWeight: 600,
                    color: m.booking_count > 0 ? '#2563eb' : '#94a3b8',
                    cursor: m.booking_count > 0 ? 'pointer' : 'default',
                    textDecoration: m.booking_count > 0 ? 'underline' : 'none',
                  }}
                >{m.booking_count}</td>
                <td
                  onClick={() => openSettlementPopup(m)}
                  style={{
                    padding: '12px 8px', textAlign: 'right', fontSize: 12,
                    color: m.total_settlement > 0 ? '#334155' : '#94a3b8',
                    cursor: m.total_settlement > 0 ? 'pointer' : 'default',
                    textDecoration: m.total_settlement > 0 ? 'underline' : 'none',
                  }}
                >
                  {m.total_settlement > 0 ? m.total_settlement.toLocaleString('ko-KR') + '원' : '-'}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  {m.role === 'admin' ? (
                    <span style={{ fontSize: 10, backgroundColor: '#6B4EFF', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>관리자</span>
                  ) : m.is_collaborator ? (
                    <span style={{ fontSize: 10, backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>공동관리</span>
                  ) : (
                    <span style={{ fontSize: 10, backgroundColor: '#f1f5f9', color: '#64748b', padding: '2px 6px', borderRadius: 4 }}>호스트</span>
                  )}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                  {m.created_at ? utcDate(m.created_at).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' }) : '-'}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center', color: '#94a3b8', fontSize: 11, whiteSpace: 'normal', lineHeight: 1.5 }}>
                  {m.last_login_at ? (
                    <>
                      <div>{utcDate(m.last_login_at).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' })}</div>
                      <div>{utcDate(m.last_login_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</div>
                    </>
                  ) : '-'}
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  {m.role !== 'admin' && (
                    <button
                      onClick={() => handleDelete(m)}
                      style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 4,
                        border: '1px solid #fca5a5', background: '#fff', color: '#dc2626',
                        cursor: 'pointer',
                      }}
                    >삭제</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
